import React from 'react';

import { Spacer } from '@zapier/design-system';

import { PublishingForm as PublishingFormType } from 'app/developer-v3/hooks';

import Steps from './Steps';
import Submission from './Submission';

import type { SemVer } from 'app/developer-v3/types/version';

type Props = {
  integrationId: number;
  isDisabled: boolean;
  onChangeVersion: (versionNext: SemVer) => void;
  onSave: (publishingFormNext: PublishingFormType) => Promise<void>;
  onSubmit: () => Promise<void>;
  publishingForm: Partial<PublishingFormType>;
  versions: SemVer[];
};

export default function PublishingForm({
  integrationId,
  isDisabled,
  onChangeVersion,
  onSave,
  onSubmit,
  publishingForm,
  versions,
}: Props) {
  const [stepsCompleted, setStepsCompleted] = React.useState({
    integrationReadiness: false,
    testAccount: false,
    appDetails: false,
    contactDetails: false,
    compliance: false,
  });

  const handleStepComplete = (
    step: keyof typeof stepsCompleted,
    isComplete: boolean
  ) => {
    setStepsCompleted(prev => ({ ...prev, [step]: isComplete }));
  };

  const hasIncompleteStep = Object.values(stepsCompleted).some(step => !step);

  return (
    <main>
      <Steps.Readiness
        initialValues={{
          hasApiOwnership: publishingForm.hasApiOwnership,
          apiEndpointsUsed: publishingForm.apiEndpointsUsed,
          ownsAllEndpoints: publishingForm.ownsAllEndpoints,
          hasPermissionForEndpoints: publishingForm.hasPermissionForEndpoints,
          usesProductionEndpoints: publishingForm.usesProductionEndpoints,
          paidApi: publishingForm.paidApi,
        }}
        isDisabled={isDisabled}
        onComplete={isComplete =>
          handleStepComplete('integrationReadiness', isComplete)
        }
        onSubmit={onSave}
      />
      <Spacer height={20} />
      <Steps.TestAccount
        initialValues={{
          testAccountUsername: publishingForm.testAccountUsername,
          testAccountPassword: publishingForm.testAccountPassword,
          testAccountOnlySupportsSso: publishingForm.testAccountOnlySupportsSso,
          ssoTestAccountCreated: publishingForm.ssoTestAccountCreated,
          testAccountNotes: publishingForm.testAccountNotes,
        }}
        isDisabled={isDisabled}
        onComplete={isComplete => handleStepComplete('testAccount', isComplete)}
        onSubmit={onSave}
      />
      <Spacer height={20} />
      <Steps.AppDetails
        initialValues={{
          hasBeenLaunched: publishingForm.hasBeenLaunched,
          isReplacementIntegration: publishingForm.isReplacementIntegration,
          alreadyManagingSimilarIntegration:
            publishingForm.alreadyManagingSimilarIntegration,
          linkToOtherIntegrations: publishingForm.linkToOtherIntegrations,
          newIntegrationRationale: publishingForm.newIntegrationRationale,
          homepageUrl: publishingForm.homepageUrl,
          apiDocsUrl: publishingForm.apiDocsUrl,
          apiDocumentationNotes: publishingForm.apiDocumentationNotes,
          primaryColor: publishingForm.primaryColor,
          twitterUsername: publishingForm.twitterUsername,
        }}
        isDisabled={isDisabled}
        onComplete={isComplete => handleStepComplete('appDetails', isComplete)}
        onSubmit={onSave}
      />
      <Spacer height={20} />
      <Steps.ContactDetails
        initialValues={{
          marketingContactEmail: publishingForm.marketingContactEmail,
          technicalContactEmail: publishingForm.technicalContactEmail,
          expectedResponseTime: publishingForm.expectedResponseTime,
        }}
        integrationId={integrationId}
        isDisabled={isDisabled}
        onComplete={isComplete =>
          handleStepComplete('contactDetails', isComplete)
        }
        onSubmit={onSave}
      />
      <Spacer height={20} />
      <Steps.Compliance
        initialValues={{
          companyBaseCountry: publishingForm.companyBaseCountry,
          transmitsSensitiveValues: publishingForm.transmitsSensitiveValues,
          collectsFinancialInfo: publishingForm.collectsFinancialInfo,
          usesThirdPartyApis: publishingForm.usesThirdPartyApis,
          permissionForThirdPartyApis:
            publishingForm.permissionForThirdPartyApis,
          usesZapierBranding: publishingForm.usesZapierBranding,
          usesCurrentZapierBranding: publishingForm.usesCurrentZapierBranding,
        }}
        isDisabled={isDisabled}
        onComplete={isComplete => handleStepComplete('compliance', isComplete)}
        onSubmit={onSave}
      />
      <Spacer height={20} />
      <Submission
        isDisabled={isDisabled || hasIncompleteStep}
        onChangeVersion={onChangeVersion}
        onSubmit={onSubmit}
        versions={versions}
      />
    </main>
  );
}
